import { useEffect, useId, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode, Ref } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from '../bootstrapJs';

import type { ModalWidth } from '../types';

export type NgModalHandle = {
  show: () => void;
  hide: () => void;
};

export type NgModalProps = {
  header?: string;
  headerTemplate?: ReactNode;
  text?: string;
  bodyTemplate?: ReactNode;
  footerTemplate?: ReactNode;
  progressTemplate?: ReactNode;
  onShown?: () => void | Promise<void>;
  dynamicWidth?: boolean;
  centered?: boolean;
  disableClosingDialog?: boolean;
  width?: ModalWidth;
  testID?: string;
  ref?: Ref<NgModalHandle>;
};

const MODAL_WIDTH_CSS: Record<ModalWidth, string> = {
  small: 'modal-sm',
  regular: '',
  medium: 'modal-md',
  large: 'modal-lg',
  extraLarge: 'modal-xl',
};

export function NgModal({
  header,
  headerTemplate,
  text,
  bodyTemplate,
  footerTemplate,
  progressTemplate,
  onShown,
  dynamicWidth = false,
  centered = false,
  disableClosingDialog,
  width = 'regular',
  testID,
  ref,
}: NgModalProps) {
  const modalElementRef = useRef<HTMLDivElement>(null);
  const headerId = useId();
  const [opened, setOpened] = useState(false);
  const shouldShowRef = useRef(false);
  const onShownRef = useRef(onShown);
  onShownRef.current = onShown;
  const disableClosingDialogRef = useRef(disableClosingDialog);
  disableClosingDialogRef.current = disableClosingDialog;
  const programmaticHideRef = useRef(false);

  useImperativeHandle(ref, () => ({
    show: () => {
      shouldShowRef.current = true;
      setOpened(true);
    },
    hide: () => {
      const element = modalElementRef.current;
      if (element) {
        programmaticHideRef.current = true;
        try {
          Modal.getInstance(element)?.hide();
        } finally {
          programmaticHideRef.current = false;
        }
      }
    },
  }));

  useEffect(() => {
    const element = modalElementRef.current;
    if (!element) {
      return;
    }

    const handleShown = () => {
      void onShownRef.current?.();
    };
    const handleHidden = () => {
      setOpened(false);
    };
    const handleHide = (event: Event) => {
      if (disableClosingDialogRef.current && !programmaticHideRef.current) event.preventDefault();
    };
    element.addEventListener('shown.bs.modal', handleShown);
    element.addEventListener('hidden.bs.modal', handleHidden);
    element.addEventListener('hide.bs.modal', handleHide);

    return () => {
      element.removeEventListener('shown.bs.modal', handleShown);
      element.removeEventListener('hidden.bs.modal', handleHidden);
      element.removeEventListener('hide.bs.modal', handleHide);
      const instance = Modal.getInstance(element);
      if (!instance) {
        return;
      }
      const disposingWhileShownWouldLeaveBodyScrollLocked = element.classList.contains('show');
      if (disposingWhileShownWouldLeaveBodyScrollLocked) {
        element.addEventListener('hidden.bs.modal', () => instance.dispose(), { once: true });
        instance.hide();
      } else {
        instance.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (opened && shouldShowRef.current) {
      shouldShowRef.current = false;
      const element = modalElementRef.current;
      if (element) {
        Modal.getOrCreateInstance(element, { keyboard: true, backdrop: true }).show();
      }
    }
  }, [opened]);

  const hasProgress = progressTemplate != null;
  useLayoutEffect(() => {
    if (hasProgress && modalElementRef.current) {
      modalElementRef.current.scrollTop = 0;
    }
  }, [hasProgress]);
  const modalSize = dynamicWidth ? 'ng-modal--dynamic-width' : '';
  const cssClass = `ng-modal ${MODAL_WIDTH_CSS[width]} ${modalSize}`;

  return createPortal(
    <div
      ref={modalElementRef}
      className={`hx-modal modal fade ${cssClass}`}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby={headerId}
      data-testid={testID}
    >
      <div className={`modal-dialog modal-fullscreen-md-down${centered ? ' modal-dialog-centered' : ''}`}>
        {opened && (
          <div className="modal-content">
            <div className="modal-header">
              <h2 id={headerId}>{header}</h2>
              {headerTemplate}
              <button
                type="button"
                className="btn-close text-reset"
                data-bs-dismiss="modal"
                aria-label="Close"
                hidden={disableClosingDialog}
              ></button>
            </div>
            <div className={`ng-modal__main${hasProgress ? ' ng-modal__main--busy' : ''}`}>
              <div className="modal-body" inert={hasProgress}>
                {text}
                {bodyTemplate}
              </div>
              <div className="modal-footer" inert={hasProgress}>
                {footerTemplate}
              </div>
              {hasProgress && <div className="ng-modal__progress">{progressTemplate}</div>}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
